// Keep the whole short clip in memory: playback never waits for another network chunk.
export const PACK_OPENING_SOURCES = Object.freeze([
  { src: 'assets/packs/reverse-pack-opening-311.mp4', type: 'video/mp4; codecs="avc1.64001f"' },
  { src: 'assets/packs/reverse-pack-opening-320.webm', type: 'video/webm; codecs="vp8"' },
]);
export const PACK_OPENING_IMAGE = 'assets/packs/reverse-pack-opening-320-animated.webp';
export const PACK_OPENING_DURATION = 4433;
const assets = new Map();
const isActive = doc => !doc.hidden && globalThis.Telegram?.WebApp?.isActive !== false;

export function supportedPackSources(video) {
  const supported = PACK_OPENING_SOURCES.filter(source => video.canPlayType(source.type));
  return supported.length ? supported : PACK_OPENING_SOURCES;
}

function loadAsset(src) {
  if (assets.has(src)) return assets.get(src);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 6000);
  const pending = fetch(src, { signal: controller.signal, cache: 'force-cache' })
    .then(response => {
      if (!response.ok) throw new Error(`Pack media: ${response.status}`);
      return response.blob();
    })
    .then(blob => ({ blob, url: URL.createObjectURL(blob) }))
    .catch(error => { assets.delete(src); throw error; })
    .finally(() => clearTimeout(timeout));
  assets.set(src, pending);
  return pending;
}

export function warmPackOpening() {
  if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const video = document.createElement('video');
  void loadAsset(supportedPackSources(video)[0].src).catch(() => {});
}

function abortable(promise, signal) {
  if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
  return new Promise((resolve, reject) => {
    const abort = () => reject(new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}

export function playBufferedPack(video, signal, onFinalFrames, onReady = () => {}) {
  if (signal.aborted) return Promise.resolve(false);
  return new Promise(resolve => {
    const doc = video.ownerDocument;
    let finished = false, finalFrames = false, moved = false, announced = false;
    let frameId = 0, visibleIdle = 0, lastTick = performance.now(), lastMediaTime = -1;
    const hasFrameCallback = typeof video.requestVideoFrameCallback === 'function';
    const finish = completed => {
      if (finished) return;
      finished = true;
      clearInterval(watchdog);
      if (frameId) video.cancelVideoFrameCallback?.(frameId);
      video.removeEventListener('ended', onEnd);
      video.removeEventListener('error', onFailure);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTime);
      doc.removeEventListener('visibilitychange', onVisibility);
      doc.removeEventListener('reverse-app-activity', onVisibility);
      signal.removeEventListener('abort', onFailure);
      video.pause();
      resolve(completed);
    };
    const progress = time => {
      if (finished || !isActive(doc)) return;
      if (time > lastMediaTime + .001) {
        visibleIdle = 0;
        lastMediaTime = time;
        moved ||= time > .02;
        if (!announced) { announced = true; onReady(); }
      }
      if (!finalFrames && moved && Number.isFinite(video.duration) && video.duration - time <= .55) {
        finalFrames = true;
        onFinalFrames();
      }
    };
    const frame = (_, metadata) => {
      progress(metadata.mediaTime);
      if (!finished) frameId = video.requestVideoFrameCallback(frame);
    };
    const onTime = () => { if (!hasFrameCallback) progress(video.currentTime); };
    const onEnd = () => finish(moved && video.currentTime >= video.duration - .2);
    const onFailure = () => finish(false);
    const onPause = () => { if (video.paused && isActive(doc) && !video.ended) onFailure(); };
    const start = () => {
      if (finished || signal.aborted || !isActive(doc)) return;
      try { Promise.resolve(video.play()).catch(onFailure); }
      catch { onFailure(); }
    };
    const onVisibility = () => {
      visibleIdle = 0;
      lastTick = performance.now();
      if (!isActive(doc)) video.pause();
      else start();
    };
    const watchdog = setInterval(() => {
      const now = performance.now();
      if (isActive(doc)) {
        visibleIdle += now - lastTick;
        // Detect an actually frozen decoder, rather than timing out a healthy clip.
        if (visibleIdle > (moved ? 1800 : 2500)) onFailure();
      }
      lastTick = now;
    }, 250);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.addEventListener('ended', onEnd);
    video.addEventListener('error', onFailure);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTime);
    doc.addEventListener('visibilitychange', onVisibility);
    doc.addEventListener('reverse-app-activity', onVisibility);
    signal.addEventListener('abort', onFailure, { once: true });
    if (hasFrameCallback) frameId = video.requestVideoFrameCallback(frame);
    start();
  });
}

async function playImagePack(image, signal, onFinalFrames, onReady) {
  const asset = await abortable(loadAsset(PACK_OPENING_IMAGE), signal);
  if (signal.aborted) return false;
  // A fresh URL restarts the one-shot image even on repeated openings.
  let url = URL.createObjectURL(asset.blob);
  return new Promise(resolve => {
    const doc = image.ownerDocument;
    let elapsed = 0, lastTick = performance.now(), loaded = false, finalFrames = false, finished = false;
    const finish = completed => {
      if (finished) return;
      finished = true;
      clearInterval(timer);
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onFailure);
      doc.removeEventListener('visibilitychange', onVisibility);
      doc.removeEventListener('reverse-app-activity', onVisibility);
      signal.removeEventListener('abort', onFailure);
      URL.revokeObjectURL(url);
      resolve(completed);
    };
    const onLoad = () => { loaded = true; elapsed = 0; lastTick = performance.now(); onReady(); };
    const onFailure = () => finish(false);
    const onVisibility = () => {
      lastTick = performance.now();
      if (isActive(doc)) {
        URL.revokeObjectURL(url);
        url = URL.createObjectURL(asset.blob);
        loaded = false; elapsed = 0; finalFrames = false;
        image.src = url;
      }
    };
    const timer = setInterval(() => {
      const now = performance.now();
      if (isActive(doc)) {
        elapsed += now - lastTick;
        if (!loaded && elapsed > 3000) return onFailure();
        if (loaded && !finalFrames && elapsed >= PACK_OPENING_DURATION - 550) {
          finalFrames = true; onFinalFrames();
        }
        if (loaded && elapsed >= PACK_OPENING_DURATION) finish(true);
      }
      lastTick = now;
    }, 50);
    image.addEventListener('load', onLoad);
    image.addEventListener('error', onFailure);
    doc.addEventListener('visibilitychange', onVisibility);
    doc.addEventListener('reverse-app-activity', onVisibility);
    signal.addEventListener('abort', onFailure, { once: true });
    image.src = url;
  });
}

export async function playPackOpening(video, image, signal, onFinalFrames, onStatus = () => {}) {
  for (const source of supportedPackSources(video)) {
    if (signal.aborted) return false;
    onStatus('loading');
    try {
      const asset = await abortable(loadAsset(source.src), signal);
      if (signal.aborted) return false;
      video.src = asset.url;
      video.load();
      const completed = await playBufferedPack(video, signal, onFinalFrames, () => onStatus('video'));
      if (completed) return true;
    } catch { /* Try a different decoder instead of skipping the tear. */ }
    video.pause();
    video.removeAttribute('src');
    video.load();
  }
  if (!signal.aborted) {
    onStatus('loading');
    try { return await playImagePack(image, signal, onFinalFrames, () => onStatus('image')); }
    catch { /* The caller offers a retry if all media failed to load. */ }
  }
  return false;
}
